import { ArrowRight } from 'lucide-react'
import type { Event } from '@/payload-types'
import { EventVolunteerHeadcountBadges } from '@/components/events/event-volunteer-headcount'
import {
  formatArtistNamesFromTalents,
  formatCalendarDayTwoDigit,
  formatCalendarMonthShort,
  formatCityStatePair,
  formatVenueCityState,
  formatWeekdayShortInTz,
  getEventEndTz,
  getEventStartTz,
  getMultiDayCount,
  getTalentsFromEvent,
  getVenueFromEvent,
} from '@/lib/event-card-helpers'
import { volunteerStatusLabel } from '@/lib/volunteer-status'
import { cn } from '@/lib/utils'

type EventStatus = NonNullable<Event['event_status']>

const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  pending: 'Pending',
  participating: 'HeadCount is Coming',
  'not-participating': 'Not Participating',
}

const EVENT_STATUS_BADGE_CLASSES: Record<EventStatus, string> = {
  pending: 'border-amber-500/30 bg-amber-500/10 text-amber-700',
  participating: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700',
  'not-participating': 'border-red-500/30 bg-red-500/10 text-red-700',
}

type VolunteerStatus = 'Pending' | 'Confirmed'

const VOLUNTEER_STATUS_BADGE_CLASSES: Record<VolunteerStatus, string> = {
  Pending: 'border-orange-500/30 bg-orange-500/10 text-orange-700',
  Confirmed: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700',
}

const badgeClassName =
  'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide'

function formatEventDate(date: string, timeZone: string): { month: string; day: string } {
  const month = formatCalendarMonthShort(date, timeZone)
  const day = formatCalendarDayTwoDigit(date, timeZone)
  if (!month || !day) {
    return { month: 'TBD', day: '--' }
  }
  return { month, day }
}

type EventCardListItemProps = {
  event: Pick<Event, 'name' | 'date'> & {
    date_tz?: string | null
    end_date?: string | null
    end_date_tz?: string | null
    event_status?: Event['event_status']
    volunteers_needed?: number | null
    talent?: Event['talent']
    venue?: Event['venue']
  }
  /** When talent relations are not populated (e.g. search JSON), pass display names in bill order. */
  artistNamesFromSearch?: string[] | null
  /** When venue is not populated on `event` (e.g. search JSON). */
  venueCityState?: { city?: string | null; state?: string | null } | null
  /** Venue display name when only search fields are available (use with `venueCityState`). */
  venueName?: string | null
  teamLeaderNames?: string | null
  subtitle?: string | null
  href?: string
  showAdvancedFeatures?: boolean
  confirmedVolunteerCount?: number | null
  volunteersNeeded?: number | null
  volunteerStatus?: VolunteerStatus | null
}

export function EventCardListItem({
  event,
  artistNamesFromSearch,
  venueCityState,
  venueName,
  teamLeaderNames,
  subtitle,
  href,
  showAdvancedFeatures = false,
  confirmedVolunteerCount,
  volunteersNeeded,
  volunteerStatus,
}: EventCardListItemProps) {
  const startTz = getEventStartTz(event)
  const endTz = getEventEndTz(event)
  const date = formatEventDate(event.date, startTz)
  const talents = getTalentsFromEvent(event)
  const artistCount = artistNamesFromSearch?.length ? artistNamesFromSearch.length : talents.length
  const artistsLine =
    artistCount > 1 ?
      artistNamesFromSearch?.length ?
        artistNamesFromSearch.join(', ')
      : formatArtistNamesFromTalents(talents)
    : null
  const venue = getVenueFromEvent(event)
  const cityStateLine =
    formatVenueCityState(venue) ??
    (venueCityState ? formatCityStatePair(venueCityState.city, venueCityState.state) : null)
  const multiDayCount = getMultiDayCount(event.date, event.end_date, startTz, endTz)
  const weekdayShort = formatWeekdayShortInTz(event.date, startTz)
  const calendarFooterLabel =
    multiDayCount != null ? `${multiDayCount} days` : weekdayShort
  const venueLabel =
    (typeof venue?.name === 'string' && venue.name.trim() ? venue.name.trim() : null) ??
    (typeof venueName === 'string' && venueName.trim() ? venueName.trim() : null)
  const status = event.event_status ?? 'pending'
  const statusLabel = EVENT_STATUS_LABELS[status]
  const resolvedConfirmedVolunteerCount =
    typeof confirmedVolunteerCount === 'number' ? confirmedVolunteerCount : 0
  const resolvedVolunteersNeeded =
    typeof volunteersNeeded === 'number' ? volunteersNeeded
    : typeof event.volunteers_needed === 'number' ? event.volunteers_needed
    : 0

  const row = (
    <div className="flex items-center gap-2 md:gap-4 px-2 py-2 md:px-6 md:py-4">
      <div className="text-theme-card-primary font-mono flex w-12.5 md:w-16.25 flex-col items-stretch rounded-sm border border-theme-card-border text-center font-bold leading-tight">
        <div className={"px-1 pt-1 text-xs"}>{date.month}</div>
        <div className="theme-card-title px-1 pb-1">{date.day}</div>
        {calendarFooterLabel && (
          <div
            className={`border-theme-card-border border-t p-1 text-xs font-bold ${
              multiDayCount != null ? 'uppercase' : ''
            }`}
          >
            {calendarFooterLabel}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm truncate font-semibold">{event.name}</p>
        {artistsLine && <p className="theme-card-content truncate text-xs">{artistsLine}</p>}
        {venueLabel && (
          <p className="theme-card-content truncate text-xs">{venueLabel}</p>
        )}
        {cityStateLine && (
          <p className="theme-card-content truncate text-xs">{cityStateLine}</p>
        )}
        {teamLeaderNames && (
          <p className="theme-card-content truncate text-xs">Team Leader: {teamLeaderNames}</p>
        )}
        {showAdvancedFeatures && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            <span className={cn(badgeClassName, EVENT_STATUS_BADGE_CLASSES[status])}>
              {statusLabel}
            </span>
            <EventVolunteerHeadcountBadges
              confirmed={resolvedConfirmedVolunteerCount}
              needed={resolvedVolunteersNeeded}
            />
          </div>
        )}
        {subtitle && <p className="theme-card-content truncate text-sm">{subtitle}</p>}
      </div>
      {volunteerStatus ?
        <span className={cn(badgeClassName, VOLUNTEER_STATUS_BADGE_CLASSES[volunteerStatus], 'shrink-0')}>
          {volunteerStatusLabel(volunteerStatus)}
        </span>
      : <ArrowRight className="size-4 md:size-6 shrink-0" />}
    </div>
  )

  return (
    <li>
      {href ?
        <a
          href={href}
          className="block transition-colors hover:bg-theme-button-primary-background"
        >
          {row}
        </a>
      : row}
    </li>
  )
}
